import './styles.css';
import { createGpuScene } from './gpu-scene.js';
import { initGithubStats } from './github-stats.js';
import { initProjects } from './projects.js';

const THEME_KEY = 'theme';

function getPreferredTheme() {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Ignore storage access errors.
  }
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function applyTheme(theme) {
  const next = theme === 'light' ? 'light' : 'dark';
  document.documentElement.setAttribute('data-theme', next);
  const meta = document.querySelector('meta[name="theme-color"]');
  const bg = next === 'light' ? '#f3f4f2' : '#0c0d0f';
  if (meta instanceof HTMLMetaElement) {
    meta.content = bg;
  }
  document.documentElement.style.colorScheme = next;
  document.documentElement.style.backgroundColor = bg;
  document.querySelectorAll('[data-theme-toggle]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    button.setAttribute(
      'aria-label',
      next === 'light' ? 'Switch to dark mode' : 'Switch to light mode',
    );
  });
  window.dispatchEvent(new CustomEvent('themechange', { detail: { theme: next } }));
  return next;
}

function initThemeToggle() {
  applyTheme(getPreferredTheme());

  document.querySelectorAll('[data-theme-toggle]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    button.addEventListener('click', () => {
      const current = document.documentElement.getAttribute('data-theme') === 'light'
        ? 'light'
        : 'dark';
      const next = applyTheme(current === 'light' ? 'dark' : 'light');
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch {
        // Ignore storage access errors.
      }
    });
  });
}

initThemeToggle();
initProjects();

const year = document.querySelector('#year');
if (year) year.textContent = new Date().getFullYear().toString();

const panel = document.querySelector('#gpu-panel');
const canvas = document.querySelector('#gpu-canvas');
const previousGpuCleanup = window.__cadenGpuSceneCleanup;
if (typeof previousGpuCleanup === 'function') previousGpuCleanup();

let gpuSceneCleanup = null;
if (panel instanceof HTMLElement && canvas instanceof HTMLCanvasElement) {
  gpuSceneCleanup = createGpuScene({ panel, canvas });
}

function releaseGpuScene() {
  const canvas = document.querySelector('#gpu-canvas');
  const panel = document.querySelector('#gpu-panel');
  if (canvas instanceof HTMLCanvasElement) {
    canvas.classList.remove('is-ready');
  }
  if (panel instanceof HTMLElement) {
    panel.classList.remove('is-ready');
  }
  gpuSceneCleanup?.();
  gpuSceneCleanup = null;
  if (window.__cadenGpuSceneCleanup === releaseGpuScene) {
    delete window.__cadenGpuSceneCleanup;
  }
}

window.__cadenGpuSceneCleanup = releaseGpuScene;
window.addEventListener('pagehide', releaseGpuScene, { once: true });
if (import.meta.hot) import.meta.hot.dispose(releaseGpuScene);

const projectFilters = document.querySelector('.project-filters');
const projectCards = document.querySelectorAll('.project-card');

if (projectFilters instanceof HTMLElement && projectCards.length > 0) {
  projectFilters.addEventListener('click', (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('[data-filter]')
      : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const filter = button.dataset.filter ?? 'all';

    projectFilters.querySelectorAll('[data-filter]').forEach((item) => {
      const active = item === button;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    projectCards.forEach((card) => {
      const categories = (card.getAttribute('data-categories') ?? '')
        .split(/\s+/)
        .filter(Boolean);
      const show = filter === 'all' || categories.includes(filter);
      card.classList.toggle('is-hidden', !show);
    });
  });
}

document.querySelectorAll('.timeline-item .read-more').forEach((button) => {
  if (!(button instanceof HTMLButtonElement)) return;

  button.addEventListener('click', () => {
    const item = button.closest('.timeline-item');
    const details = button.getAttribute('aria-controls')
      ? document.getElementById(button.getAttribute('aria-controls'))
      : null;
    if (!(item instanceof HTMLElement) || !(details instanceof HTMLElement)) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    button.textContent = expanded ? 'Read more' : 'Show less';
    details.hidden = expanded;
    item.classList.toggle('is-expanded', !expanded);
  });
});

const githubRoot = document.querySelector('[data-github-stats]');
if (githubRoot instanceof HTMLElement) {
  initGithubStats(githubRoot);
}
